import { useEffect, useState } from "react";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from "@/components/ui/table";
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from "@/components/ui/select";
import { ShieldCheck, Users, Briefcase, Calendar, DollarSign, Building2, FileText, Eye, Wallet, Banknote, PlayCircle, Flag, Star, MessageSquare, Phone, Copy, X, RefreshCw, Download, Clock, Sparkles } from "lucide-react";
import { toast } from "sonner";
import { format, parseISO } from "date-fns";
import { AuthImage } from "@/components/FileUpload";
import SLAClock from "@/components/SLAClock";
import AutonomyScoreCard from "@/components/AutonomyScoreCard";

export default function AdminDashboard() {
    const [stats, setStats] = useState(null);
    const [finance, setFinance] = useState(null);
    const [analytics, setAnalytics] = useState(null);
    const [users, setUsers] = useState([]);
    const [providers, setProviders] = useState([]);
    const [bookings, setBookings] = useState([]);
    const [venues, setVenues] = useState([]);
    const [venueInvoices, setVenueInvoices] = useState({}); // venue_id -> [invoices]
    const [previewProvider, setPreviewProvider] = useState(null);
    const [reminderProvider, setReminderProvider] = useState(null);
    const [payoutChannels, setPayoutChannels] = useState({ available: [], active: [] });
    const [ownerRevenue, setOwnerRevenue] = useState(null);
    const [pendingPayouts, setPendingPayouts] = useState([]);
    const [payoutBatches, setPayoutBatches] = useState([]);
    const [batchItems, setBatchItems] = useState({});
    const [reviewQueue, setReviewQueue] = useState([]);
    const [reviewFilter, setReviewFilter] = useState("reported");
    const [disputes, setDisputes] = useState([]);
    const [disputeFilter, setDisputeFilter] = useState("open");
    const [handoffs, setHandoffs] = useState([]);
    const [handoffFilter, setHandoffFilter] = useState("open");
    const [leads, setLeads] = useState([]);
    const [leadStats, setLeadStats] = useState(null);
    // CEO-friendly default: hide test/seed accounts so the dashboard shows
    // only real customer + provider sign-ups. Toggleable from the header.
    const [hideTest, setHideTest] = useState(true);
    const [loading, setLoading] = useState(true);
    const [lastSyncedAt, setLastSyncedAt] = useState(null);

    const refresh = () => {
        setLoading(true);
        const q = hideTest ? "?hide_test=true" : "";
        // Customers tab gets a strict role filter so the Pros and Customers
        // rosters never bleed into each other (CEO export-friendly).
        const customersQ = `?role=customer${hideTest ? "&hide_test=true" : ""}`;
        Promise.all([
            api.get(`/admin/stats${q}`).then((r) => setStats(r.data)),
            api.get("/admin/finance/summary").then((r) => setFinance(r.data)).catch(() => setFinance(null)),
            api.get("/admin/analytics/summary?days=30").then((r) => setAnalytics(r.data)).catch(() => setAnalytics(null)),
            api.get(`/admin/users${customersQ}`).then((r) => setUsers(r.data)),
            api.get(`/admin/providers${q}`).then((r) => setProviders(r.data)),
            api.get("/admin/bookings").then((r) => setBookings(r.data)),
            api.get("/admin/venues").then((r) => setVenues(r.data)),
            api.get("/admin/payout-channels").then((r) => setPayoutChannels(r.data)),
            api.get("/admin/owner-revenue").then((r) => setOwnerRevenue(r.data)),
            api.get("/admin/payouts/pending").then((r) => setPendingPayouts(r.data)),
            api.get("/admin/payouts/batches").then((r) => setPayoutBatches(r.data)),
            api.get(`/admin/reviews?status=${reviewFilter}`).then((r) => setReviewQueue(r.data)).catch(() => setReviewQueue([])),
            api.get(`/admin/disputes${disputeFilter === "all" ? "" : `?status=${disputeFilter}`}`).then((r) => setDisputes(r.data)).catch(() => setDisputes([])),
            api.get(`/admin/concierge/handoffs${handoffFilter === "all" ? "" : `?status=${handoffFilter}`}`).then((r) => setHandoffs(r.data)).catch(() => setHandoffs([])),
            api.get("/admin/leads").then((r) => setLeads(r.data)).catch(() => setLeads([])),
            api.get("/admin/leads/stats").then((r) => setLeadStats(r.data)).catch(() => setLeadStats(null)),
        ]).finally(() => {
            setLoading(false);
            setLastSyncedAt(new Date());
        });
    };

    useEffect(() => {
        refresh();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [hideTest]);

    // Live sync — auto-refresh every 25s while the tab is visible so new
    // sign-ups (customers + pros) appear on the CEO's screen without a manual
    // reload. Pauses while the tab is hidden to avoid wasted polling.
    useEffect(() => {
        const POLL_MS = 25_000;
        const tick = () => {
            if (document.visibilityState === "visible") refresh();
        };
        const id = setInterval(tick, POLL_MS);
        const onVis = () => {
            if (document.visibilityState === "visible") refresh();
        };
        document.addEventListener("visibilitychange", onVis);
        return () => {
            clearInterval(id);
            document.removeEventListener("visibilitychange", onVis);
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [hideTest]);

    useEffect(() => {
        api.get(`/admin/reviews?status=${reviewFilter}`)
            .then((r) => setReviewQueue(r.data))
            .catch(() => setReviewQueue([]));
    }, [reviewFilter]);

    useEffect(() => {
        api.get(`/admin/disputes${disputeFilter === "all" ? "" : `?status=${disputeFilter}`}`)
            .then((r) => setDisputes(r.data || []))
            .catch(() => setDisputes([]));
    }, [disputeFilter]);

    useEffect(() => {
        api.get(`/admin/concierge/handoffs${handoffFilter === "all" ? "" : `?status=${handoffFilter}`}`)
            .then((r) => setHandoffs(r.data || []))
            .catch(() => setHandoffs([]));
    }, [handoffFilter]);

    const updateHandoff = async (id, status, notes = "") => {
        try {
            await api.patch(`/admin/concierge/handoffs/${id}`, { status, admin_notes: notes });
            toast.success(`Ticket marked ${status.replace("_", " ")}.`);
            const r = await api.get(`/admin/concierge/handoffs${handoffFilter === "all" ? "" : `?status=${handoffFilter}`}`);
            setHandoffs(r.data || []);
        } catch (e) {
            toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
        }
    };

    const resolveDispute = async (id, resolution, adminNotes = "") => {
        try {
            await api.patch(`/admin/disputes/${id}`, { resolution, admin_notes: adminNotes });
            toast.success(`Dispute marked as ${resolution.replace("_", " ")}.`);
            const r = await api.get(`/admin/disputes${disputeFilter === "all" ? "" : `?status=${disputeFilter}`}`);
            setDisputes(r.data || []);
        } catch (e) {
            toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
        }
    };

    const moderateReview = async (id, action) => {
        try {
            await api.patch(`/admin/reviews/${id}`, { action });
            const labels = { hide: "Review hidden", restore: "Review restored", dismiss: "Reports dismissed" };
            toast.success(labels[action] || "Done");
            const r = await api.get(`/admin/reviews?status=${reviewFilter}`);
            setReviewQueue(r.data);
        } catch (e) {
            toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
        }
    };

    const verifyProvider = async (id) => {
        try {
            await api.patch(`/providers/${id}/verify`);
            await api.patch(`/providers/${id}/application-status`, { application_status: "approved" });
            toast.success("Provider approved & verified");
            refresh();
        } catch (e) {
            toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
        }
    };

    const setTier = async (id, tier) => {
        try {
            await api.patch(`/providers/${id}/tier`, { tier });
            toast.success(`Tier set to ${tier}`);
            refresh();
        } catch (e) {
            toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
        }
    };

    const assignProvider = async (bookingId, providerId) => {
        try {
            await api.patch(`/bookings/${bookingId}/assign`, { provider_id: providerId });
            toast.success("Provider assigned");
            refresh();
        } catch (e) {
            toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
        }
    };

    const setVenueStatus = async (id, status) => {
        try {
            await api.patch(`/admin/venues/${id}/status`, { status });
            toast.success(`Venue ${status}`);
            refresh();
        } catch (e) {
            toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
        }
    };

    const generateVenueInvoice = async (id) => {
        try {
            const { data } = await api.post(`/admin/venues/${id}/invoice`);
            toast.success(`Invoice generated · JMD ${Math.round(data.net_due).toLocaleString()}`);
            loadVenueInvoices(id);
            refresh();
        } catch (e) {
            toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
        }
    };

    const loadVenueInvoices = async (id) => {
        try {
            const { data } = await api.get(`/admin/venues/${id}/invoices`);
            setVenueInvoices((prev) => ({ ...prev, [id]: data }));
        } catch (e) { /* noop */ }
    };

    const markInvoicePaid = async (invoiceId, venueId) => {
        try {
            await api.patch(`/admin/venue-invoices/${invoiceId}/paid`);
            toast.success("Invoice marked as paid");
            loadVenueInvoices(venueId);
        } catch (e) {
            toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
        }
    };

    const togglePayoutChannel = async (channel) => {
        const next = payoutChannels.active.includes(channel)
            ? payoutChannels.active.filter((c) => c !== channel)
            : [...payoutChannels.active, channel];
        try {
            await api.patch("/admin/payout-channels", { channels: next });
            setPayoutChannels({ ...payoutChannels, active: next });
        } catch (e) {
            toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
        }
    };

    const runWeeklyBatch = async () => {
        try {
            const { data } = await api.post("/admin/payouts/run-weekly");
            toast.success(`Batch created · ${data.items_count} payouts · JMD ${Math.round(data.total_amount).toLocaleString()}`);
            refresh();
        } catch (e) {
            toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
        }
    };

    const loadBatchItems = async (batchId) => {
        try {
            const { data } = await api.get(`/admin/payouts/batches/${batchId}/items`);
            setBatchItems((prev) => ({ ...prev, [batchId]: data }));
        } catch { /* noop */ }
    };

    const markPayoutPaid = async (itemId, batchId) => {
        try {
            await api.patch(`/admin/payouts/items/${itemId}/paid`);
            toast.success("Marked paid");
            loadBatchItems(batchId);
            api.get("/admin/payouts/batches").then((r) => setPayoutBatches(r.data));
            api.get("/admin/payouts/pending").then((r) => setPendingPayouts(r.data));
        } catch (e) {
            toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
        }
    };

    // -------------------------------------------------------------------
    // CSV export — distinct files for Customers and Pros so the CEO can
    // share/email each list independently. Generated entirely client-side
    // from the data already loaded (no extra round-trip required).
    // -------------------------------------------------------------------
    const downloadCsv = (filename, rows, columns) => {
        if (!rows || rows.length === 0) {
            toast.error("Nothing to export — list is empty.");
            return;
        }
        const escape = (val) => {
            if (val === null || val === undefined) return "";
            const s = Array.isArray(val) ? val.join("; ") : String(val);
            if (/[",\n]/.test(s)) return `"${s.replace(/"/g, '""')}"`;
            return s;
        };
        const header = columns.map((c) => c.label).join(",");
        const body = rows
            .map((r) => columns.map((c) => escape(c.get(r))).join(","))
            .join("\n");
        const blob = new Blob([header + "\n" + body], { type: "text/csv;charset=utf-8;" });
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        const stamp = new Date().toISOString().slice(0, 10);
        a.download = `${filename}_${stamp}.csv`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(url);
        toast.success(`${rows.length} row${rows.length === 1 ? "" : "s"} exported`);
    };

    const exportCustomersCsv = () => {
        downloadCsv("smart_customers", users, [
            { label: "Name", get: (u) => u.name },
            { label: "Email", get: (u) => u.email },
            { label: "Phone", get: (u) => u.phone || "" },
            { label: "Loyalty Points", get: (u) => u.loyalty_points || 0 },
            { label: "VIP Tier", get: (u) => u.vip_tier || "" },
            { label: "Email Verified", get: (u) => (u.email_verified ? "yes" : "no") },
            { label: "Joined", get: (u) => u.created_at || "" },
        ]);
    };

    const exportProvidersCsv = () => {
        downloadCsv("smart_pros", providers, [
            { label: "Name", get: (p) => p.name },
            { label: "Email", get: (p) => p.email },
            { label: "Phone", get: (p) => p.phone || "" },
            { label: "Categories", get: (p) => p.service_categories || [] },
            { label: "Locations", get: (p) => p.locations || [] },
            { label: "Hourly Rate (JMD)", get: (p) => p.hourly_rate || "" },
            { label: "Application Status", get: (p) => p.application_status_label || p.application_status || "" },
            { label: "Verification Level", get: (p) => p.verification_label || "" },
            { label: "Days Remaining (Grace)", get: (p) => (p.days_remaining ?? "") },
            { label: "Suspension Reason", get: (p) => p.suspension_reason || "" },
            { label: "Verified", get: (p) => (p.verified ? "yes" : "no") },
            { label: "Missing Quick-Start Docs", get: (p) => (p.missing_quick || []).length },
            { label: "Missing Hard Docs", get: (p) => (p.missing_hard || []).length },
            { label: "Rating", get: (p) => p.rating ?? "" },
            { label: "Completed Jobs", get: (p) => p.completed_jobs || 0 },
            { label: "Joined", get: (p) => p.created_at || "" },
        ]);
    };

    const exportLeadsCsv = () => {
        downloadCsv("smart_leads", leads, [
            { label: "Email", get: (l) => l.email || "" },
            { label: "Phone", get: (l) => l.phone || "" },
            { label: "Source", get: (l) => l.source || "" },
            { label: "Promo Code", get: (l) => l.promo_code || "" },
            { label: "Nudge Email Sent", get: (l) => (l.nudge_email_sent ? "yes" : "no") },
            { label: "Converted", get: (l) => (l.converted_to_user ? "yes" : "no") },
            { label: "Touches", get: (l) => l.touches || 1 },
            { label: "Captured", get: (l) => l.created_at || "" },
        ]);
    };

    return (
        <div className="min-h-screen bg-background" data-testid="admin-dashboard">
            <Header />

            <section className="max-w-7xl mx-auto px-6 lg:px-10 py-12 lg:py-16">
                <div className="flex items-start justify-between gap-6 mb-12 flex-wrap">
                    <div className="flex items-center gap-5">
                        <img
                            src="/smart-logo.jpeg"
                            alt="SMART"
                            className="h-14 w-14 object-contain rounded-sm border border-primary/30 shadow-[0_0_24px_rgba(200,162,75,0.15)]"
                            data-testid="admin-logo"
                        />
                        <div>
                            <div className="overline mb-3">Operations Console</div>
                            <h1 className="font-serif text-5xl tracking-tighter">
                                SMART <span className="italic gold-grad">command.</span>
                            </h1>
                        </div>
                    </div>

                    <div className="flex items-center gap-3 flex-wrap">
                        <div
                            className="text-[10px] uppercase tracking-[0.25em] text-muted-foreground inline-flex items-center gap-2"
                            data-testid="last-synced-indicator"
                        >
                            <span className={`inline-block w-1.5 h-1.5 rounded-full ${loading ? "bg-amber-400 animate-pulse" : "bg-emerald-400"}`} />
                            {loading
                                ? "Syncing…"
                                : `Live · synced ${lastSyncedAt ? format(lastSyncedAt, "HH:mm:ss") : "—"}`}
                        </div>
                        <Button
                            type="button"
                            size="sm"
                            variant="outline"
                            onClick={refresh}
                            disabled={loading}
                            className="rounded-sm uppercase tracking-[0.2em] text-[10px] h-9 border-white/15"
                            data-testid="admin-refresh-btn"
                        >
                            <RefreshCw size={12} className={`mr-1 ${loading ? "animate-spin" : ""}`} />
                            Refresh
                        </Button>
                        <button
                            type="button"
                            onClick={() => setHideTest((v) => !v)}
                            className={`group inline-flex items-center gap-3 rounded-sm border px-4 py-3 transition-colors ${
                                hideTest
                                    ? "border-primary/40 bg-primary/10 text-primary"
                                    : "border-white/10 bg-card text-muted-foreground hover:border-white/20"
                            }`}
                            data-testid="hide-test-toggle"
                            aria-pressed={hideTest}
                        >
                            <span
                                className={`relative inline-block w-9 h-5 rounded-full transition-colors ${
                                    hideTest ? "bg-primary" : "bg-white/15"
                                }`}
                                aria-hidden="true"
                            >
                                <span
                                    className={`absolute top-0.5 left-0.5 w-4 h-4 rounded-full bg-background transition-transform ${
                                        hideTest ? "translate-x-4" : "translate-x-0"
                                    }`}
                                />
                            </span>
                            <span className="text-[10px] uppercase tracking-[0.25em]">
                                Hide Test Data
                            </span>
                        </button>
                    </div>
                </div>

                {/* Autonomy score (how much ran this week without you) */}
                <div className="mb-10">
                    <AutonomyScoreCard />
                </div>

                {/* Stats */}
                <div className="grid grid-cols-2 lg:grid-cols-5 gap-px bg-white/5 mb-16" data-testid="admin-stats">
                    <StatCard icon={Users} label="Customers" value={stats?.customers || 0} />
                    <StatCard icon={Briefcase} label="Providers" value={stats?.providers || 0} />
                    <StatCard icon={ShieldCheck} label="Verified Pros" value={stats?.verified_providers || 0} />
                    <StatCard icon={Calendar} label="Bookings" value={stats?.bookings || 0} />
                    <StatCard
                        icon={DollarSign}
                        label="Revenue (USD)"
                        value={`$${(stats?.revenue_usd || 0).toLocaleString()}`}
                    />
                </div>

                {/* Visitor analytics — CEO wanted avg time on site */}
                <div className="grid grid-cols-2 lg:grid-cols-4 gap-px bg-white/5 mb-16" data-testid="admin-visitor-stats">
                    <StatCard
                        icon={Clock}
                        label="Avg time on site"
                        value={analytics?.avg_duration_human || "—"}
                        testid="stat-avg-time"
                    />
                    <StatCard
                        icon={Users}
                        label="Sessions (30d)"
                        value={analytics?.sessions ?? 0}
                        testid="stat-sessions"
                    />
                    <StatCard
                        icon={Sparkles}
                        label="Long visits (3m+)"
                        value={analytics?.long_visits ?? 0}
                        testid="stat-long-visits"
                    />
                    <StatCard
                        icon={ShieldCheck}
                        label="Bounce rate"
                        value={`${analytics?.bounce_rate_pct ?? 0}%`}
                        testid="stat-bounce"
                    />
                </div>

                <Tabs defaultValue="bookings" className="w-full">
                    <TabsList className="bg-card border border-white/10 rounded-sm p-1 mb-8">
                        <TabsTrigger
                            value="bookings"
                            className="data-[state=active]:bg-primary data-[state=active]:text-primary-foreground rounded-sm uppercase tracking-[0.2em] text-xs"
                            data-testid="admin-tab-bookings"
                        >
                            Bookings
                        </TabsTrigger>
                        <TabsTrigger
                            value="finance"
                            className="data-[state=active]:bg-primary data-[state=active]:text-primary-foreground rounded-sm uppercase tracking-[0.2em] text-xs"
                            data-testid="admin-tab-finance"
                        >
                            Wallet
                        </TabsTrigger>
                        <TabsTrigger
                            value="providers"
                            className="data-[state=active]:bg-primary data-[state=active]:text-primary-foreground rounded-sm uppercase tracking-[0.2em] text-xs"
                            data-testid="admin-tab-providers"
                        >
                            Providers
                        </TabsTrigger>
                        <TabsTrigger
                            value="users"
                            className="data-[state=active]:bg-primary data-[state=active]:text-primary-foreground rounded-sm uppercase tracking-[0.2em] text-xs"
                            data-testid="admin-tab-users"
                        >
                            Customers
                        </TabsTrigger>
                        <TabsTrigger
                            value="venues"
                            className="data-[state=active]:bg-primary data-[state=active]:text-primary-foreground rounded-sm uppercase tracking-[0.2em] text-xs"
                            data-testid="admin-tab-venues"
                        >
                            Venues
                        </TabsTrigger>
                        <TabsTrigger
                            value="payouts"
                            className="data-[state=active]:bg-primary data-[state=active]:text-primary-foreground rounded-sm uppercase tracking-[0.2em] text-xs"
                            data-testid="admin-tab-payouts"
                        >
                            Payouts
                        </TabsTrigger>
                        <TabsTrigger
                            value="leads"
                            className="data-[state=active]:bg-primary data-[state=active]:text-primary-foreground rounded-sm uppercase tracking-[0.2em] text-xs"
                            data-testid="admin-tab-leads"
                        >
                            Leads
                            {leads.length > 0 && (
                                <span className="ml-2 inline-flex items-center justify-center min-w-[18px] h-[18px] px-1 rounded-full bg-primary/30 gold-text text-[10px] font-semibold">
                                    {leads.length}
                                </span>
                            )}
                        </TabsTrigger>
                        <TabsTrigger
                            value="moderation"
                            className="data-[state=active]:bg-primary data-[state=active]:text-primary-foreground rounded-sm uppercase tracking-[0.2em] text-xs"
                            data-testid="admin-tab-moderation"
                        >
                            Moderation
                            {reviewQueue.length > 0 && reviewFilter === "reported" && (
                                <span className="ml-2 inline-flex items-center justify-center min-w-[18px] h-[18px] px-1 rounded-full bg-destructive text-destructive-foreground text-[10px] font-semibold">
                                    {reviewQueue.length}
                                </span>
                            )}
                        </TabsTrigger>
                        <TabsTrigger
                            value="disputes"
                            className="data-[state=active]:bg-primary data-[state=active]:text-primary-foreground rounded-sm uppercase tracking-[0.2em] text-xs"
                            data-testid="admin-tab-disputes"
                        >
                            Disputes
                            {disputes.filter((d) => d.status === "open").length > 0 && (
                                <span className="ml-2 inline-flex items-center justify-center min-w-[18px] h-[18px] px-1 rounded-full bg-destructive text-destructive-foreground text-[10px] font-semibold">
                                    {disputes.filter((d) => d.status === "open").length}
                                </span>
                            )}
                        </TabsTrigger>
                        <TabsTrigger
                            value="concierge"
                            className="data-[state=active]:bg-primary data-[state=active]:text-primary-foreground rounded-sm uppercase tracking-[0.2em] text-xs"
                            data-testid="admin-tab-concierge"
                        >
                            Concierge
                            {handoffs.filter((h) => h.status === "open").length > 0 && (
                                <span className="ml-2 inline-flex items-center justify-center min-w-[18px] h-[18px] px-1 rounded-full bg-destructive text-destructive-foreground text-[10px] font-semibold">
                                    {handoffs.filter((h) => h.status === "open").length}
                                </span>
                            )}
                        </TabsTrigger>
                    </TabsList>

                    <TabsContent value="finance">
                        <div className="bg-card border border-white/10 p-6 lg:p-8" data-testid="admin-finance-tab">
                            <div className="flex items-baseline justify-between mb-8">
                                <div>
                                    <div className="overline mb-2">Owner Wallet · {finance?.month || "—"}</div>
                                    <h2 className="font-serif text-3xl tracking-tight">
                                        Monthly <span className="italic gold-grad">summary.</span>
                                    </h2>
                                </div>
                                <div className="text-right">
                                    <div className="overline">Platform net</div>
                                    <div className="font-serif text-4xl mt-1 gold-text" data-testid="finance-platform-net">
                                        ${(finance?.platform_net || 0).toLocaleString()}
                                    </div>
                                    <div className="text-[10px] text-muted-foreground uppercase tracking-[0.2em]">JMD</div>
                                </div>
                            </div>

                            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
                                <FinanceCard label="Paid bookings" value={finance?.paid_bookings ?? 0} testid="finance-bookings" />
                                <FinanceCard
                                    label="Gross revenue"
                                    value={`$${(finance?.gross_revenue || 0).toLocaleString()}`}
                                    testid="finance-gross"
                                />
                                <FinanceCard
                                    label="Royal Protection collected"
                                    value={`$${(finance?.royal_protection_collected || 0).toLocaleString()}`}
                                    testid="finance-royal-protection"
                                    accent="gold"
                                />
                                <FinanceCard
                                    label="Processing fees collected"
                                    value={`$${(finance?.processing_fees_collected || 0).toLocaleString()}`}
                                    testid="finance-processing"
                                />
                                <FinanceCard
                                    label="VIP-waived fees (absorbed)"
                                    value={`$${(finance?.processing_fees_waived_est || 0).toLocaleString()}`}
                                    testid="finance-vip-waived"
                                    accent="warn"
                                />
                                <FinanceCard
                                    label="VIP-covered bookings"
                                    value={finance?.vip_waived_bookings ?? 0}
                                    testid="finance-vip-count"
                                />
                                <FinanceCard
                                    label="Provider payouts owed"
                                    value={`$${(finance?.provider_payouts_owed || 0).toLocaleString()}`}
                                    testid="finance-payouts"
                                />
                            </div>

                            <div className="mt-8 text-xs text-muted-foreground max-w-2xl leading-relaxed">
                                <strong className="gold-text">Platform net</strong> = Royal Protection collected − Processing fees the platform absorbed for VIP members. Everything else settles through HandyPay into your First Global Bank account.
                            </div>
                        </div>
                    </TabsContent>

                    <TabsContent value="bookings">
                        <div className="bg-card border border-white/10 overflow-x-auto">
                            <Table data-testid="admin-bookings-table">
                                <TableHeader>
                                    <TableRow className="border-white/10 hover:bg-transparent">
                                        <TableHead className="overline">Service</TableHead>
                                        <TableHead className="overline">Customer</TableHead>
                                        <TableHead className="overline">Provider</TableHead>
                                        <TableHead className="overline">Schedule</TableHead>
                                        <TableHead className="overline">Status</TableHead>
                                        <TableHead className="overline">Payment</TableHead>
                                        <TableHead className="overline">Total</TableHead>
                                        <TableHead className="overline">Actions</TableHead>
                                    </TableRow>
                                </TableHeader>
                                <TableBody>
                                    {bookings.map((b) => (
                                        <TableRow key={b.id} className="border-white/5">
                                            <TableCell className="font-medium">{b.service_name}</TableCell>
                                            <TableCell className="text-muted-foreground">{b.customer_name}</TableCell>
                                            <TableCell className="text-muted-foreground">
                                                {b.provider_name || "—"}
                                            </TableCell>
                                            <TableCell className="text-xs text-muted-foreground">
                                                {format(parseISO(b.scheduled_at), "MMM d · HH:mm")}
                                            </TableCell>
                                            <TableCell>
                                                <Badge className="bg-secondary text-foreground border-white/10 uppercase text-[10px] tracking-[0.2em]">
                                                    {b.status.replace("_", " ")}
                                                </Badge>
                                            </TableCell>
                                            <TableCell>
                                                <Badge
                                                    variant="outline"
                                                    className={`uppercase text-[10px] tracking-[0.2em] border ${
                                                        b.payment_status === "paid"
                                                            ? "border-emerald-700/40 text-emerald-300"
                                                            : "border-white/10 text-muted-foreground"
                                                    }`}
                                                >
                                                    {b.payment_status}
                                                </Badge>
                                            </TableCell>
                                            <TableCell>
                                                ${b.total_amount.toLocaleString()} {b.currency}
                                            </TableCell>
                                            <TableCell>
                                                {!b.provider_id && providers.filter((p) => p.verified).length > 0 && (
                                                    <Select
                                                        onValueChange={(pid) => assignProvider(b.id, pid)}
                                                    >
                                                        <SelectTrigger
                                                            className="bg-input border-white/10 h-9 rounded-sm w-40"
                                                            data-testid={`assign-${b.id}`}
                                                        >
                                                            <SelectValue placeholder="Assign pro..." />
                                                        </SelectTrigger>
                                                        <SelectContent className="bg-card border-white/10">
                                                            {providers
                                                                .filter((p) => p.verified)
                                                                .map((p) => (
                                                                    <SelectItem key={p.id} value={p.id}>
                                                                        {p.name}
                                                                    </SelectItem>
                                                                ))}
                                                        </SelectContent>
                                                    </Select>
                                                )}
                                            </TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                            {bookings.length === 0 && !loading && (
                                <div className="p-12 text-center text-muted-foreground">No bookings yet.</div>
                            )}
                        </div>
                    </TabsContent>

                    <TabsContent value="providers">
                        <div className="flex items-center justify-between mb-4 flex-wrap gap-3">
                            <div className="text-xs text-muted-foreground">
                                <span className="text-foreground font-medium" data-testid="providers-count">{providers.length}</span> pro{providers.length === 1 ? "" : "s"} on file
                            </div>
                            <Button
                                size="sm"
                                variant="outline"
                                onClick={exportProvidersCsv}
                                disabled={providers.length === 0}
                                className="rounded-sm uppercase tracking-[0.2em] text-[10px] h-8 border-white/15"
                                data-testid="export-pros-csv"
                            >
                                <Download size={12} className="mr-1" /> Export Pros CSV
                            </Button>
                        </div>
                        <div className="bg-card border border-white/10 overflow-x-auto">
                            <Table data-testid="admin-providers-table">
                                <TableHeader>
                                    <TableRow className="border-white/10 hover:bg-transparent">
                                        <TableHead className="overline">Name</TableHead>
                                        <TableHead className="overline">Email</TableHead>
                                        <TableHead className="overline">Phone</TableHead>
                                        <TableHead className="overline">Categories</TableHead>
                                        <TableHead className="overline">Locations</TableHead>
                                        <TableHead className="overline">Rate (JMD/hr)</TableHead>
                                        <TableHead className="overline">Verification</TableHead>
                                        <TableHead className="overline">Status</TableHead>
                                        <TableHead className="overline">Action</TableHead>
                                    </TableRow>
                                </TableHeader>
                                <TableBody>
                                    {providers.map((p) => (
                                        <TableRow key={p.user_id} className="border-white/5">
                                            <TableCell className="font-medium">
                                                {p.name}
                                                {p.is_test && (
                                                    <Badge className="ml-2 bg-white/5 text-muted-foreground border border-white/10 uppercase text-[9px] tracking-[0.2em]">
                                                        Test
                                                    </Badge>
                                                )}
                                            </TableCell>
                                            <TableCell className="text-muted-foreground text-xs">{p.email}</TableCell>
                                            <TableCell className="text-xs">
                                                <PhoneCell phone={p.phone} name={p.name} />
                                            </TableCell>
                                            <TableCell className="text-xs text-muted-foreground">
                                                {(p.service_categories || []).length === 0 ? "—" : (p.service_categories || []).join(", ")}
                                            </TableCell>
                                            <TableCell className="text-xs text-muted-foreground">
                                                {(p.locations || []).length === 0 ? "—" : (p.locations || []).join(", ")}
                                            </TableCell>
                                            <TableCell>{p.hourly_rate ? `$${p.hourly_rate.toLocaleString()}` : "—"}</TableCell>
                                            <TableCell>
                                                <VerificationBadge level={p.verification_level} label={p.verification_label} daysRemaining={p.days_remaining} />
                                            </TableCell>
                                            <TableCell>
                                                {!p.application_submitted ? (
                                                    <Badge className="bg-white/5 text-muted-foreground border border-white/10 uppercase text-[10px] tracking-[0.2em]">
                                                        Awaiting application
                                                    </Badge>
                                                ) : p.application_status === "approved" ? (
                                                    <Badge className="bg-emerald-900/40 text-emerald-300 border border-emerald-700/40 uppercase text-[10px] tracking-[0.2em]">
                                                        Approved
                                                    </Badge>
                                                ) : p.application_status === "rejected" ? (
                                                    <Badge className="bg-rose-900/40 text-rose-300 border border-rose-700/40 uppercase text-[10px] tracking-[0.2em]">
                                                        Rejected
                                                    </Badge>
                                                ) : p.application_status === "missing_documents" ? (
                                                    <Badge className="bg-rose-900/30 text-rose-300 border border-rose-700/40 uppercase text-[10px] tracking-[0.2em]">
                                                        Missing {p.missing_required?.length || 0} doc{(p.missing_required?.length || 0) === 1 ? "" : "s"}
                                                    </Badge>
                                                ) : (
                                                    <Badge className="bg-amber-900/30 text-amber-300 border border-amber-700/40 uppercase text-[10px] tracking-[0.2em]">
                                                        Pending review
                                                    </Badge>
                                                )}
                                            </TableCell>
                                            <TableCell>
                                                <div className="flex flex-wrap gap-2 items-center">
                                                    {p.application_submitted && !p.verified && (
                                                        <Button
                                                            size="sm"
                                                            onClick={() => verifyProvider(p.id)}
                                                            className="rounded-sm uppercase tracking-[0.2em] text-[10px] h-8 bg-primary text-primary-foreground"
                                                            data-testid={`verify-${p.id}`}
                                                        >
                                                            Approve
                                                        </Button>
                                                    )}
                                                    {p.application_submitted && (
                                                        <>
                                                            <Button
                                                                size="sm"
                                                                variant="outline"
                                                                onClick={() => setPreviewProvider(p)}
                                                                className="rounded-sm uppercase tracking-[0.2em] text-[10px] h-8 border-white/15"
                                                                data-testid={`view-docs-${p.id}`}
                                                            >
                                                                <Eye size={12} className="mr-1" /> Docs
                                                            </Button>
                                                            <Select onValueChange={(t) => setTier(p.id, t)}>
                                                                <SelectTrigger className="bg-input border-white/10 h-8 rounded-sm w-28 text-[10px]" data-testid={`tier-select-${p.id}`}>
                                                                    <SelectValue placeholder="Set tier" />
                                                                </SelectTrigger>
                                                                <SelectContent className="bg-card border-white/10">
                                                                    <SelectItem value="starter">Starter</SelectItem>
                                                                    <SelectItem value="medium">Medium</SelectItem>
                                                                    <SelectItem value="expert">Expert</SelectItem>
                                                                </SelectContent>
                                                            </Select>
                                                        </>
                                                    )}
                                                    {!p.application_submitted && (
                                                        <Button
                                                            size="sm"
                                                            onClick={() => setReminderProvider(p)}
                                                            className="rounded-sm uppercase tracking-[0.2em] text-[10px] h-8 bg-secondary text-foreground hover:bg-white/10 border border-white/10"
                                                            data-testid={`send-reminder-${p.user_id}`}
                                                        >
                                                            <MessageSquare size={12} className="mr-1" /> Send Reminder
                                                        </Button>
                                                    )}
                                                </div>
                                            </TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                            {providers.length === 0 && !loading && (
                                <div className="p-12 text-center text-muted-foreground">No providers yet.</div>
                            )}
                        </div>
                    </TabsContent>

                    <TabsContent value="users">
                        <div className="flex items-center justify-between mb-4 flex-wrap gap-3">
                            <div className="text-xs text-muted-foreground">
                                <span className="text-foreground font-medium" data-testid="customers-count">{users.length}</span> customer{users.length === 1 ? "" : "s"} on file · pros excluded
                            </div>
                            <Button
                                size="sm"
                                variant="outline"
                                onClick={exportCustomersCsv}
                                disabled={users.length === 0}
                                className="rounded-sm uppercase tracking-[0.2em] text-[10px] h-8 border-white/15"
                                data-testid="export-customers-csv"
                            >
                                <Download size={12} className="mr-1" /> Export Customers CSV
                            </Button>
                        </div>
                        <div className="bg-card border border-white/10 overflow-x-auto">
                            <Table data-testid="admin-users-table">
                                <TableHeader>
                                    <TableRow className="border-white/10 hover:bg-transparent">
                                        <TableHead className="overline">Name</TableHead>
                                        <TableHead className="overline">Email</TableHead>
                                        <TableHead className="overline">Role</TableHead>
                                        <TableHead className="overline">Loyalty Pts</TableHead>
                                        <TableHead className="overline">VIP</TableHead>
                                        <TableHead className="overline">Joined</TableHead>
                                    </TableRow>
                                </TableHeader>
                                <TableBody>
                                    {users.map((u) => (
                                        <TableRow key={u.id} className="border-white/5">
                                            <TableCell className="font-medium">
                                                {u.name}
                                                {u.is_test && (
                                                    <Badge className="ml-2 bg-white/5 text-muted-foreground border border-white/10 uppercase text-[9px] tracking-[0.2em]">
                                                        Test
                                                    </Badge>
                                                )}
                                            </TableCell>
                                            <TableCell className="text-muted-foreground text-xs">{u.email}</TableCell>
                                            <TableCell>
                                                <Badge className="bg-secondary text-foreground border-white/10 uppercase text-[10px] tracking-[0.2em]">
                                                    {u.role}
                                                </Badge>
                                            </TableCell>
                                            <TableCell>{u.loyalty_points || 0}</TableCell>
                                            <TableCell className="text-xs gold-text">
                                                {u.vip_tier?.replace("vip_", "").toUpperCase() || "—"}
                                            </TableCell>
                                            <TableCell className="text-xs text-muted-foreground">
                                                {u.created_at ? format(parseISO(u.created_at), "MMM d, yyyy") : "—"}
                                            </TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                            {users.length === 0 && !loading && (
                                <div className="p-12 text-center text-muted-foreground">No customers yet.</div>
                            )}
                        </div>
                    </TabsContent>

                    <TabsContent value="venues">
                        <div className="space-y-4" data-testid="admin-venues-content">
                            {venues.length === 0 && !loading && (
                                <div className="p-12 text-center text-muted-foreground bg-card border border-white/10">No venue partners yet.</div>
                            )}
                            {venues.map((v) => (
                                <div key={v.id} className="bg-card border border-white/10 rounded-sm p-6">
                                    <div className="flex flex-wrap items-start justify-between gap-4">
                                        <div className="flex items-start gap-4">
                                            <div className="w-12 h-12 grid place-items-center bg-input border border-white/10 rounded-sm gold-text">
                                                <Building2 size={20} strokeWidth={1.5} />
                                            </div>
                                            <div>
                                                <div className="font-serif text-xl">{v.name}</div>
                                                <div className="text-xs text-muted-foreground mt-1">
                                                    {v.venue_type.toUpperCase()} · {v.city} · {v.rooms || "—"} rooms
                                                </div>
                                                <div className="text-xs text-muted-foreground">
                                                    Contact: {v.contact_name} · {v.contact_email}
                                                </div>
                                                <div className="text-[11px] text-muted-foreground mt-1">
                                                    Code: <span className="gold-text font-mono">{v.invite_code}</span>
                                                    {v.affiliate_rate_override !== null && v.affiliate_rate_override !== undefined && (
                                                        <span className="ml-3">Custom rate: {(v.affiliate_rate_override * 100).toFixed(0)}%</span>
                                                    )}
                                                </div>
                                            </div>
                                        </div>
                                        <div className="flex flex-wrap items-center gap-2">
                                            <Badge className={`uppercase text-[10px] tracking-[0.2em] ${
                                                v.status === "approved" ? "bg-emerald-900/30 text-emerald-300 border border-emerald-700/40" :
                                                v.status === "suspended" ? "bg-red-900/30 text-red-300 border border-red-700/40" :
                                                "bg-amber-900/30 text-amber-300 border border-amber-700/40"
                                            }`}>{v.status}</Badge>
                                            {v.status !== "approved" && (
                                                <Button
                                                    size="sm"
                                                    onClick={() => setVenueStatus(v.id, "approved")}
                                                    className="bg-primary text-primary-foreground rounded-sm uppercase tracking-[0.2em] text-[10px] h-8"
                                                    data-testid={`venue-approve-${v.id}`}
                                                >
                                                    Approve
                                                </Button>
                                            )}
                                            {v.status === "approved" && (
                                                <Button
                                                    size="sm"
                                                    variant="outline"
                                                    onClick={() => setVenueStatus(v.id, "suspended")}
                                                    className="rounded-sm uppercase tracking-[0.2em] text-[10px] h-8 border-white/15"
                                                    data-testid={`venue-suspend-${v.id}`}
                                                >
                                                    Suspend
                                                </Button>
                                            )}
                                            <Button
                                                size="sm"
                                                variant="outline"
                                                onClick={() => generateVenueInvoice(v.id)}
                                                className="rounded-sm uppercase tracking-[0.2em] text-[10px] h-8 border-white/15"
                                                data-testid={`venue-invoice-${v.id}`}
                                            >
                                                <FileText size={12} className="mr-1" /> Generate Invoice
                                            </Button>
                                            <Button
                                                size="sm"
                                                variant="outline"
                                                onClick={() => loadVenueInvoices(v.id)}
                                                className="rounded-sm uppercase tracking-[0.2em] text-[10px] h-8 border-white/15"
                                                data-testid={`venue-load-invoices-${v.id}`}
                                            >
                                                Load Invoices
                                            </Button>
                                        </div>
                                    </div>
                                    {venueInvoices[v.id] && venueInvoices[v.id].length > 0 && (
                                        <div className="mt-4 border-t border-white/10 pt-4 space-y-2">
                                            {venueInvoices[v.id].map((inv) => (
                                                <div key={inv.id} className="flex flex-wrap items-center justify-between gap-3 text-xs bg-input p-3 rounded-sm">
                                                    <span className="font-mono">{inv.id.slice(0,8).toUpperCase()}</span>
                                                    <span className="text-muted-foreground">{inv.booking_count} bookings</span>
                                                    <span>Net: <span className="gold-text">JMD ${Math.round(inv.net_due).toLocaleString()}</span></span>
                                                    <span className={inv.status === "paid" ? "text-emerald-400" : "text-amber-300"}>{inv.status}</span>
                                                    {inv.status !== "paid" && (
                                                        <Button
                                                            size="sm"
                                                            onClick={() => markInvoicePaid(inv.id, v.id)}
                                                            className="rounded-sm uppercase tracking-[0.2em] text-[10px] h-7 bg-primary text-primary-foreground"
                                                            data-testid={`mark-invoice-paid-${inv.id}`}
                                                        >
                                                            Mark Paid
                                                        </Button>
                                                    )}
                                                </div>
                                            ))}
                                        </div>
                                    )}
                                </div>
                            ))}
                        </div>
                    </TabsContent>

                    <TabsContent value="payouts">
                        <PayoutsTab
                            channels={payoutChannels}
                            ownerRevenue={ownerRevenue}
                            pending={pendingPayouts}
                            batches={payoutBatches}
                            batchItems={batchItems}
                            onToggleChannel={togglePayoutChannel}
                            onRunWeekly={runWeeklyBatch}
                            onLoadBatch={loadBatchItems}
                            onMarkPaid={markPayoutPaid}
                        />
                    </TabsContent>

                    <TabsContent value="leads">
                        <LeadsTab
                            leads={leads}
                            stats={leadStats}
                            onExport={exportLeadsCsv}
                        />
                    </TabsContent>

                    <TabsContent value="moderation">
                        <ModerationTab
                            queue={reviewQueue}
                            filter={reviewFilter}
                            onFilter={setReviewFilter}
                            onAction={moderateReview}
                        />
                    </TabsContent>

                    <TabsContent value="disputes">
                        <DisputesTab
                            disputes={disputes}
                            filter={disputeFilter}
                            onFilter={setDisputeFilter}
                            onResolve={resolveDispute}
                        />
                    </TabsContent>

                    <TabsContent value="concierge">
                        <ConciergeTab
                            handoffs={handoffs}
                            filter={handoffFilter}
                            onFilter={setHandoffFilter}
                            onUpdate={updateHandoff}
                        />
                    </TabsContent>
                </Tabs>
            </section>

            {previewProvider && (
                <ProviderDocsModal provider={previewProvider} onClose={() => setPreviewProvider(null)} />
            )}
            {reminderProvider && (
                <ReminderModal provider={reminderProvider} onClose={() => setReminderProvider(null)} />
            )}

            <Footer />
        </div>
    );
}

function ProviderDocsModal({ provider, onClose }) {
    // Use the canonical document array returned by the backend if present —
    // it includes BOTH uploaded and missing docs with the correct
    // required-flag treatment. Falls back to legacy synthesized list.
    const docs = (provider.documents && provider.documents.length)
        ? provider.documents.map((d) => ({
            label: d.label,
            id: d.url,
            uploaded: d.uploaded,
            required: d.required,
        }))
        : [
            { label: "Profile picture", id: provider.profile_picture_url, uploaded: !!provider.profile_picture_url },
            { label: "Government ID", id: provider.id_document_url, required: true, uploaded: !!provider.id_document_url },
            { label: "Police Certificate", id: provider.police_record_url, required: true, uploaded: !!provider.police_record_url },
            { label: "TRN document", id: provider.trn_url, required: true, uploaded: !!provider.trn_url },
            { label: "Proof of Address", id: provider.proof_of_address_url, required: true, uploaded: !!provider.proof_of_address_url },
            { label: "Trade license", id: provider.license_url, uploaded: !!provider.license_url },
        ];
    const certs = (provider.certificates || []).map((c, i) => ({ label: `Certificate #${i + 1}`, id: c, uploaded: !!c }));
    const portfolio = (provider.portfolio || []).map((p, i) => ({ label: `Portfolio #${i + 1}`, id: p, uploaded: !!p }));
    const allDocs = [...docs, ...certs, ...portfolio];

    const missingCount = docs.filter((d) => d.required && !d.uploaded).length;
    const uploadedCount = allDocs.filter((d) => d.uploaded).length;

    return (
        <div className="fixed inset-0 z-50 grid place-items-center bg-black/80 p-4" onClick={onClose} data-testid="provider-docs-modal">
            <div className="bg-card border border-primary/30 rounded-sm max-w-3xl w-full max-h-[85vh] overflow-y-auto p-6" onClick={(e) => e.stopPropagation()}>
                <div className="flex items-start justify-between mb-6 gap-4">
                    <div className="flex-1 min-w-0">
                        <div className="overline">Verification</div>
                        <h3 className="font-serif text-2xl">{provider.name}</h3>
                        <div className="mt-3 grid sm:grid-cols-2 gap-3">
                            <div className="text-xs">
                                <div className="overline mb-1">Email</div>
                                <div className="text-foreground/90 break-all">{provider.email || "—"}</div>
                            </div>
                            <div className="text-xs">
                                <div className="overline mb-1">Phone</div>
                                <div data-testid="docs-modal-phone">
                                    <PhoneCell phone={provider.phone} name={provider.name} />
                                </div>
                            </div>
                        </div>
                        <div className="mt-3 flex items-center gap-2 flex-wrap">
                            <Badge className={`uppercase text-[10px] tracking-[0.2em] rounded-sm ${
                                missingCount > 0
                                    ? "bg-rose-500/20 text-rose-200 border border-rose-500/50 font-bold"
                                    : "bg-emerald-500/15 text-emerald-300 border border-emerald-500/30"
                            }`}>
                                {missingCount > 0 ? `${missingCount} REQUIRED DOCUMENT${missingCount === 1 ? "" : "S"} MISSING` : "All required documents on file"}
                            </Badge>
                            <span className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground">
                                {uploadedCount} uploaded
                            </span>
                        </div>
                    </div>
                    <button onClick={onClose} className="text-muted-foreground hover:text-foreground shrink-0" data-testid="provider-docs-close">
                        <X size={18} />
                    </button>
                </div>
                <div className="grid sm:grid-cols-2 gap-4">
                    {allDocs.map((d) => (
                        <DocPreview key={`doc-${d.label}`} label={d.label} fileId={d.id} uploaded={d.uploaded} required={d.required} />
                    ))}
                    {allDocs.length === 0 && <div className="text-sm text-muted-foreground col-span-2">No documents on file.</div>}
                </div>
            </div>
        </div>
    );
}

function DocPreview({ label, fileId, uploaded, required }) {
    const isUrl = typeof fileId === "string" && (fileId.startsWith("http://") || fileId.startsWith("https://"));
    if (!uploaded) {
        return (
            <div className={`rounded-sm overflow-hidden border-2 ${required ? "border-rose-500 bg-rose-500/10" : "border-white/10 bg-card"}`}>
                <div className={`px-3 py-2 text-[11px] uppercase tracking-[0.2em] flex items-center justify-between gap-2 border-b ${required ? "text-rose-200 border-rose-500/40 font-bold" : "text-muted-foreground border-white/10"}`}>
                    <span>{label}</span>
                    <span className={required ? "font-extrabold text-rose-300" : ""}>{required ? "MISSING" : "Not provided"}</span>
                </div>
                <div className={`aspect-[4/3] grid place-items-center text-center px-4 ${required ? "bg-rose-500/10 text-rose-200" : "bg-black/40 text-muted-foreground"}`}>
                    <div className={`text-[10px] uppercase tracking-[0.25em] ${required ? "font-semibold" : ""}`}>
                        {required ? "Required document not uploaded" : "Optional · awaiting upload"}
                    </div>
                </div>
            </div>
        );
    }
    return (
        <div className="bg-input border border-white/10 rounded-sm overflow-hidden">
            <div className="px-3 py-2 text-[10px] uppercase tracking-[0.2em] text-muted-foreground border-b border-white/10 flex items-center justify-between">
                <span>{label}</span>
                <span className="text-emerald-300">Uploaded</span>
            </div>
            <div className="aspect-[4/3] bg-black/40 grid place-items-center">
                {isUrl ? (
                    <img src={fileId} alt={label} className="w-full h-full object-cover" />
                ) : (
                    <AuthImage fileId={fileId} alt={label} className="w-full h-full object-cover" />
                )}
            </div>
        </div>
    );
}

function StatCard({ icon: Icon, label, value }) {
    return (
        <div className="bg-background p-6 lg:p-8">
            <Icon size={20} strokeWidth={1} className="gold-text mb-4" />
            <div className="overline mb-2">{label}</div>
            <div className="font-serif text-2xl lg:text-3xl">{value}</div>
        </div>
    );
}

const CHANNEL_LABELS = {
    stripe: { name: "Stripe Express", desc: "USD/JMD direct deposit · 2-day rolling" },
    ncb_bank: { name: "NCB Bank", desc: "Manual JMD wire · CSV reconciliation" },
    scotia_bank: { name: "Scotia Bank", desc: "Manual JMD wire · CSV reconciliation" },
    wipay: { name: "WiPay", desc: "Caribbean processor · JMD settlement" },
    lynk: { name: "Lynk Wallet", desc: "NCB Quisk · mobile money" },
    paypal: { name: "PayPal Business", desc: "USD · global" },
    wise: { name: "Wise Business", desc: "Multi-currency · low FX" },
    crypto_usdc: { name: "Crypto (USDC)", desc: "Stablecoin · instant" },
};

function PayoutsTab({ channels, ownerRevenue, pending, batches, batchItems, onToggleChannel, onRunWeekly, onLoadBatch, onMarkPaid }) {
    const totalPending = pending.reduce((s, p) => s + (p.pending || 0), 0);
    return (
        <div className="space-y-10" data-testid="admin-payouts-content">
            <div className="grid sm:grid-cols-3 gap-4">
                <RevenueCard label="Platform commission" value={ownerRevenue?.platform_commission_jmd} sub={`${ownerRevenue?.paid_bookings || 0} paid bookings`} />
                <RevenueCard label="Venue invoices collected" value={ownerRevenue?.venue_invoiced_jmd} sub={`${ownerRevenue?.paid_invoices || 0} invoices paid`} />
                <RevenueCard label="Total received (JMD)" value={ownerRevenue?.total_jmd} sub="All channels combined" highlight />
            </div>

            <div className="bg-card border border-white/10 rounded-sm p-6">
                <div className="flex items-center gap-2 mb-4">
                    <Wallet size={16} className="gold-text" strokeWidth={1.5} />
                    <h3 className="font-serif text-2xl">Owner payout channels</h3>
                </div>
                <p className="text-xs text-muted-foreground mb-6">
                    Toggle the channels you accept for owner-side collections (platform commission + venue invoices).
                </p>
                <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-3" data-testid="owner-channels-grid">
                    {(channels.available || []).map((c) => {
                        const active = channels.active?.includes(c);
                        const label = CHANNEL_LABELS[c] || { name: c, desc: "" };
                        return (
                            <button
                                key={c}
                                onClick={() => onToggleChannel(c)}
                                className={`text-left p-4 border rounded-sm transition-colors ${
                                    active ? "border-primary bg-primary/10" : "border-white/10 bg-input hover:border-white/20"
                                }`}
                                data-testid={`channel-${c}`}
                            >
                                <div className="flex items-center justify-between">
                                    <div className={`text-sm font-semibold ${active ? "text-primary" : "text-foreground"}`}>{label.name}</div>
                                    <div className={`w-3 h-3 rounded-full ${active ? "bg-primary" : "bg-white/10"}`} />
                                </div>
                                <div className="text-[11px] text-muted-foreground mt-1">{label.desc}</div>
                            </button>
                        );
                    })}
                </div>
            </div>

            <div className="bg-card border border-white/10 rounded-sm p-6">
                <div className="flex flex-wrap items-center justify-between gap-4 mb-4">
                    <div className="flex items-center gap-2">
                        <Banknote size={16} className="gold-text" strokeWidth={1.5} />
                        <h3 className="font-serif text-2xl">Provider payouts pending</h3>
                    </div>
                    <div className="flex items-center gap-3 text-xs">
                        <span className="text-muted-foreground">Total pending:</span>
                        <span className="gold-text font-serif text-lg">JMD ${Math.round(totalPending).toLocaleString()}</span>
                        <Button
                            onClick={onRunWeekly}
                            disabled={pending.length === 0}
                            className="bg-primary text-primary-foreground rounded-sm uppercase tracking-[0.2em] text-xs h-9"
                            data-testid="run-weekly-payouts-btn"
                        >
                            <PlayCircle size={14} className="mr-2" /> Run Weekly Batch
                        </Button>
                    </div>
                </div>
                {pending.length === 0 ? (
                    <div className="text-sm text-muted-foreground py-8 text-center">No pending payouts.</div>
                ) : (
                    <Table data-testid="admin-pending-payouts-table">
                        <TableHeader>
                            <TableRow className="border-white/10 hover:bg-transparent">
                                <TableHead className="overline">Provider</TableHead>
                                <TableHead className="overline">Bank</TableHead>
                                <TableHead className="overline">Jobs</TableHead>
                                <TableHead className="overline">Earned</TableHead>
                                <TableHead className="overline">Paid out</TableHead>
                                <TableHead className="overline">Pending (JMD)</TableHead>
                            </TableRow>
                        </TableHeader>
                        <TableBody>
                            {pending.map((p) => (
                                <TableRow key={p.provider_id} className="border-white/5">
                                    <TableCell>
                                        <div className="font-medium">{p.name}</div>
                                        <div className="text-xs text-muted-foreground">{p.email}</div>
                                    </TableCell>
                                    <TableCell className="text-xs text-muted-foreground">
                                        {p.bank_details ? (
                                            <>
                                                <div>{p.bank_details.bank_name}</div>
                                                <div>{p.bank_details.account_number} · {p.bank_details.account_type}</div>
                                            </>
                                        ) : (
                                            <span className="text-amber-400">No bank on file</span>
                                        )}
                                    </TableCell>
                                    <TableCell>{p.completed_jobs}</TableCell>
                                    <TableCell>${Math.round(p.earned).toLocaleString()}</TableCell>
                                    <TableCell>${Math.round(p.paid_out).toLocaleString()}</TableCell>
                                    <TableCell><span className="gold-text">${Math.round(p.pending).toLocaleString()}</span></TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                )}
            </div>

            <div className="bg-card border border-white/10 rounded-sm p-6">
                <div className="flex items-center gap-2 mb-4">
                    <FileText size={16} className="gold-text" strokeWidth={1.5} />
                    <h3 className="font-serif text-2xl">Batch history</h3>
                </div>
                {batches.length === 0 ? (
                    <div className="text-sm text-muted-foreground py-8 text-center">No batches generated yet.</div>
                ) : (
                    <div className="space-y-3" data-testid="payout-batches-list">
                        {batches.map((b) => (
                            <div key={b.id} className="border border-white/10 rounded-sm">
                                <div className="flex flex-wrap items-center justify-between gap-3 p-4">
                                    <div className="flex items-center gap-3">
                                        <span className="font-mono text-xs">{b.id.slice(0, 8).toUpperCase()}</span>
                                        <Badge className={`uppercase text-[10px] tracking-[0.2em] ${
                                            b.status === "completed" ? "bg-emerald-900/30 text-emerald-300 border border-emerald-700/40" : "bg-amber-900/30 text-amber-300 border border-amber-700/40"
                                        }`}>{b.status}</Badge>
                                        <span className="text-xs text-muted-foreground">{new Date(b.created_at).toLocaleDateString()}</span>
                                    </div>
                                    <div className="flex items-center gap-4">
                                        <span className="text-xs text-muted-foreground">{b.items_count} items</span>
                                        <span className="font-serif gold-text">JMD ${Math.round(b.total_amount).toLocaleString()}</span>
                                        <Button
                                            size="sm"
                                            variant="outline"
                                            onClick={() => onLoadBatch(b.id)}
                                            className="rounded-sm uppercase tracking-[0.2em] text-[10px] h-8 border-white/15"
                                            data-testid={`load-batch-${b.id}`}
                                        >
                                            Items
                                        </Button>
                                    </div>
                                </div>
                                {batchItems[b.id] && (
                                    <div className="border-t border-white/10 p-4 space-y-2">
                                        {batchItems[b.id].map((it) => (
                                            <div key={it.id} className="flex flex-wrap items-center justify-between gap-3 text-xs bg-input p-3 rounded-sm">
                                                <span>{it.provider_name}</span>
                                                <span className="text-muted-foreground">
                                                    {it.bank_details?.bank_name || "—"} · {it.bank_details?.account_number || "no acct"}
                                                </span>
                                                <span className="gold-text font-serif">JMD ${Math.round(it.amount).toLocaleString()}</span>
                                                {it.status === "paid" ? (
                                                    <span className="text-emerald-400 uppercase tracking-[0.2em] text-[10px]">Paid</span>
                                                ) : (
                                                    <Button
                                                        size="sm"
                                                        onClick={() => onMarkPaid(it.id, b.id)}
                                                        className="rounded-sm uppercase tracking-[0.2em] text-[10px] h-7 bg-primary text-primary-foreground"
                                                        data-testid={`mark-paid-${it.id}`}
                                                    >
                                                        Mark Paid
                                                    </Button>
                                                )}
                                            </div>
                                        ))}
                                    </div>
                                )}
                            </div>
                        ))}
                    </div>
                )}
            </div>
        </div>
    );
}

function RevenueCard({ label, value, sub, highlight }) {
    return (
        <div className={`p-6 border rounded-sm ${highlight ? "bg-primary/10 border-primary/40" : "bg-card border-white/10"}`}>
            <div className="overline">{label}</div>
            <div className="font-serif text-3xl gold-text mt-3">JMD ${Math.round(value || 0).toLocaleString()}</div>
            {sub && <div className="text-[11px] text-muted-foreground mt-2">{sub}</div>}
        </div>
    );
}

function ModerationTab({ queue, filter, onFilter, onAction }) {
    return (
        <div className="space-y-6" data-testid="moderation-tab">
            <div className="flex items-center justify-between gap-4">
                <div>
                    <div className="overline mb-1">Review moderation</div>
                    <h3 className="font-serif text-2xl">
                        {filter === "reported" && "Reported reviews"}
                        {filter === "hidden" && "Hidden reviews"}
                        {filter === "all" && "All reviews"}
                    </h3>
                </div>
                <Select value={filter} onValueChange={onFilter}>
                    <SelectTrigger
                        className="w-44 bg-card border-white/10 rounded-sm uppercase tracking-[0.2em] text-xs h-10"
                        data-testid="moderation-filter"
                    >
                        <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                        <SelectItem value="reported">Reported only</SelectItem>
                        <SelectItem value="hidden">Hidden</SelectItem>
                        <SelectItem value="all">All reviews</SelectItem>
                    </SelectContent>
                </Select>
            </div>

            {queue.length === 0 ? (
                <div className="p-12 bg-card border border-white/5 text-center text-muted-foreground rounded-sm" data-testid="moderation-empty">
                    <Flag size={32} strokeWidth={1} className="gold-text mx-auto mb-4" />
                    {filter === "reported"
                        ? "No reported reviews. All clear."
                        : filter === "hidden"
                            ? "No hidden reviews."
                            : "No reviews yet."}
                </div>
            ) : (
                <div className="space-y-4">
                    {queue.map((b) => (
                        <article
                            key={b.id}
                            className="p-6 bg-card border border-white/10 rounded-sm"
                            data-testid={`moderation-row-${b.id}`}
                        >
                            <div className="flex items-start justify-between gap-4 mb-3 flex-wrap">
                                <div className="flex-1 min-w-[260px]">
                                    <div className="flex items-center gap-2 mb-2 flex-wrap">
                                        <Badge className="uppercase text-[10px] tracking-[0.2em] rounded-sm bg-secondary text-foreground border-white/10">
                                            {b.service_name}
                                        </Badge>
                                        {b.review_hidden && (
                                            <Badge className="uppercase text-[10px] tracking-[0.2em] rounded-sm bg-destructive/20 text-destructive border-destructive/40">
                                                Hidden
                                            </Badge>
                                        )}
                                        {(b.review_report_count || 0) > 0 && !b.review_hidden && (
                                            <Badge className="uppercase text-[10px] tracking-[0.2em] rounded-sm bg-yellow-500/15 text-yellow-300 border-yellow-500/30 inline-flex items-center gap-1">
                                                <Flag size={10} strokeWidth={1.5} /> {b.review_report_count} report{b.review_report_count === 1 ? "" : "s"}
                                            </Badge>
                                        )}
                                    </div>
                                    <div className="text-xs text-muted-foreground">
                                        {b.customer_name} → {b.provider_name}
                                        {b.reviewed_at && ` · ${format(parseISO(b.reviewed_at), "MMM d, yyyy")}`}
                                    </div>
                                </div>
                                <div className="flex items-center gap-1 gold-text">
                                    {[1, 2, 3, 4, 5].map((n) => (
                                        <Star
                                            key={`admin-star-${b.id}-${n}`}
                                            size={14}
                                            className={n <= b.rating ? "fill-current" : "opacity-30"}
                                            strokeWidth={1.5}
                                        />
                                    ))}
                                </div>
                            </div>

                            {b.review_comment && (
                                <p className="text-sm text-foreground/90 mb-3 leading-relaxed">
                                    "{b.review_comment}"
                                </p>
                            )}

                            {b.provider_reply && (
                                <div className="mt-3 pl-4 border-l-2 border-primary/40 bg-primary/5 p-3 rounded-sm mb-3">
                                    <div className="overline mb-1.5 inline-flex items-center gap-2">
                                        <MessageSquare size={11} strokeWidth={1.5} /> Provider replied
                                    </div>
                                    <p className="text-sm text-foreground/90">{b.provider_reply.text}</p>
                                </div>
                            )}

                            {Array.isArray(b.review_reports) && b.review_reports.length > 0 && (
                                <div className="mt-3 p-3 bg-yellow-500/5 border border-yellow-500/20 rounded-sm">
                                    <div className="overline mb-2 text-yellow-300/80">Reports</div>
                                    <ul className="space-y-1.5 text-xs">
                                        {b.review_reports.map((r, i) => (
                                            <li key={`report-${b.id}-${r.reported_at || i}`} className="text-muted-foreground" data-testid={`report-entry-${b.id}-${i}`}>
                                                <span className="text-foreground">{r.user_name}</span> · {r.reason}
                                                <span className="text-muted-foreground/60 ml-2">
                                                    ({format(parseISO(r.reported_at), "MMM d, HH:mm")})
                                                </span>
                                            </li>
                                        ))}
                                    </ul>
                                </div>
                            )}

                            <div className="mt-4 pt-3 border-t border-white/5 flex flex-wrap gap-2 justify-end">
                                {!b.review_hidden && (
                                    <Button
                                        size="sm"
                                        onClick={() => onAction(b.id, "hide")}
                                        className="rounded-sm uppercase tracking-[0.2em] text-[10px] h-9 bg-destructive text-destructive-foreground hover:bg-destructive/90"
                                        data-testid={`moderation-hide-${b.id}`}
                                    >
                                        Hide
                                    </Button>
                                )}
                                {b.review_hidden && (
                                    <Button
                                        size="sm"
                                        onClick={() => onAction(b.id, "restore")}
                                        className="rounded-sm uppercase tracking-[0.2em] text-[10px] h-9 bg-emerald-700 text-white hover:bg-emerald-600"
                                        data-testid={`moderation-restore-${b.id}`}
                                    >
                                        Restore
                                    </Button>
                                )}
                                {(b.review_report_count || 0) > 0 && (
                                    <Button
                                        size="sm"
                                        onClick={() => onAction(b.id, "dismiss")}
                                        className="rounded-sm uppercase tracking-[0.2em] text-[10px] h-9 bg-secondary text-foreground hover:bg-white/10 border border-white/10"
                                        data-testid={`moderation-dismiss-${b.id}`}
                                    >
                                        Dismiss reports
                                    </Button>
                                )}
                            </div>
                        </article>
                    ))}
                </div>
            )}
        </div>
    );
}



// ---------------------------------------------------------------------------
// Helpers — phone formatting + WhatsApp deep-link
// ---------------------------------------------------------------------------

/** Strip everything except digits; if the number lacks a country code, assume
 *  Jamaica (+1-876-...). Returns the bare digit string suitable for wa.me. */
function toWhatsAppDigits(phone) {
    if (!phone) return "";
    const d = String(phone).replace(/\D/g, "");
    if (!d) return "";
    if (d.startsWith("1")) return d;
    if (d.length === 10) return `1${d}`; // bare 876XXXXXXX → +1 876
    if (d.length === 7) return `1876${d}`; // 7-digit → +1 876
    return d;
}

function PhoneCell({ phone, name }) {
    if (!phone) return <span className="text-muted-foreground">—</span>;
    const wa = toWhatsAppDigits(phone);
    const message = encodeURIComponent(
        `Hi ${name || ""}, this is the SMART Jamaica admin team. We noticed you started signing up — drop us a line whenever you have a minute so we can finish onboarding you. customercare@smartdemands247.com`
    );
    const link = `https://wa.me/${wa}?text=${message}`;
    return (
        <span className="inline-flex items-center gap-2">
            <span className="text-foreground/90">{phone}</span>
            <a
                href={link}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center justify-center w-6 h-6 rounded-full bg-emerald-600/15 hover:bg-emerald-600/30 text-emerald-300 transition-colors"
                aria-label={`Open WhatsApp chat with ${name}`}
                data-testid="whatsapp-link"
                title="WhatsApp"
            >
                <svg viewBox="0 0 24 24" fill="currentColor" className="w-3 h-3" aria-hidden="true">
                    <path d="M19.05 4.91A10 10 0 0 0 4.43 17.45L3.5 21l3.65-.96a10 10 0 0 0 14.84-12.04 10 10 0 0 0-2.94-3.09zm-7.05 14.17a8.4 8.4 0 0 1-4.3-1.18l-.31-.18-2.16.57.58-2.1-.2-.32a8.4 8.4 0 1 1 6.4 3.21zm4.6-6.3c-.25-.13-1.5-.74-1.73-.83-.23-.08-.4-.13-.56.13-.16.25-.65.83-.8 1-.15.16-.3.18-.55.06-.25-.13-1.07-.4-2.04-1.26a7.7 7.7 0 0 1-1.42-1.77c-.15-.26 0-.4.11-.52.11-.11.25-.3.38-.45.13-.15.16-.26.25-.43.08-.18.04-.33-.02-.46-.06-.13-.56-1.36-.77-1.86-.2-.5-.4-.42-.55-.43h-.47a.9.9 0 0 0-.66.3c-.23.25-.86.85-.86 2.07s.88 2.4 1 2.57c.13.18 1.74 2.65 4.2 3.71.59.25 1.05.4 1.41.51.59.19 1.13.16 1.55.1.47-.07 1.5-.61 1.71-1.21.21-.6.21-1.11.15-1.21-.06-.1-.23-.16-.49-.28z" />
                </svg>
            </a>
        </span>
    );
}

function ReminderModal({ provider, onClose }) {
    const [copied, setCopied] = useState(false);
    const wa = toWhatsAppDigits(provider.phone);
    const waLink = wa
        ? `https://wa.me/${wa}?text=${encodeURIComponent(
            `Hi ${provider.name}, this is SMART Jamaica admin. We noticed you started your provider sign-up but haven't finished. Tap https://smartdemands247.com/become-provider to upload your documents (ID, police record, TRN, proof of address) and get verified.`
        )}`
        : null;

    const copyEmail = async () => {
        try {
            await navigator.clipboard.writeText(provider.email);
            setCopied(true);
            toast.success("Email copied to clipboard");
            setTimeout(() => setCopied(false), 1500);
        } catch (e) {
            toast.error("Couldn't copy — please select manually");
        }
    };

    return (
        <div
            className="fixed inset-0 z-50 grid place-items-center bg-black/80 p-4"
            onClick={onClose}
            data-testid="reminder-modal"
        >
            <div
                className="bg-card border border-primary/30 rounded-sm max-w-md w-full p-6"
                onClick={(e) => e.stopPropagation()}
            >
                <div className="flex items-start justify-between mb-6">
                    <div>
                        <div className="overline">Manual outreach</div>
                        <h3 className="font-serif text-2xl">{provider.name}</h3>
                        <p className="text-xs text-muted-foreground mt-2">
                            Reach out directly to nudge them to finish their application.
                        </p>
                    </div>
                    <button onClick={onClose} className="text-muted-foreground hover:text-foreground" data-testid="reminder-close">
                        <X size={18} />
                    </button>
                </div>

                <div className="space-y-3">
                    <div>
                        <div className="overline mb-2">Email</div>
                        <div className="flex items-center gap-2">
                            <code className="flex-1 px-3 py-2 bg-input border border-white/10 rounded-sm text-sm text-foreground/90 break-all">
                                {provider.email || "—"}
                            </code>
                            {provider.email && (
                                <Button
                                    size="sm"
                                    onClick={copyEmail}
                                    className="rounded-sm uppercase tracking-[0.2em] text-[10px] h-9 bg-primary text-primary-foreground"
                                    data-testid="reminder-copy-email"
                                >
                                    <Copy size={12} className="mr-1" />
                                    {copied ? "Copied" : "Copy"}
                                </Button>
                            )}
                        </div>
                    </div>

                    <div>
                        <div className="overline mb-2">Phone</div>
                        {provider.phone ? (
                            <div className="flex items-center gap-2 flex-wrap">
                                <code className="px-3 py-2 bg-input border border-white/10 rounded-sm text-sm text-foreground/90">
                                    {provider.phone}
                                </code>
                                {waLink && (
                                    <a
                                        href={waLink}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="inline-flex items-center gap-2 px-3 h-9 rounded-sm bg-emerald-600/15 hover:bg-emerald-600/30 text-emerald-300 uppercase tracking-[0.2em] text-[10px]"
                                        data-testid="reminder-whatsapp-link"
                                    >
                                        <svg viewBox="0 0 24 24" fill="currentColor" className="w-3 h-3" aria-hidden="true">
                                            <path d="M19.05 4.91A10 10 0 0 0 4.43 17.45L3.5 21l3.65-.96a10 10 0 0 0 14.84-12.04 10 10 0 0 0-2.94-3.09zm-7.05 14.17a8.4 8.4 0 0 1-4.3-1.18l-.31-.18-2.16.57.58-2.1-.2-.32a8.4 8.4 0 1 1 6.4 3.21zm4.6-6.3c-.25-.13-1.5-.74-1.73-.83-.23-.08-.4-.13-.56.13-.16.25-.65.83-.8 1-.15.16-.3.18-.55.06-.25-.13-1.07-.4-2.04-1.26a7.7 7.7 0 0 1-1.42-1.77c-.15-.26 0-.4.11-.52.11-.11.25-.3.38-.45.13-.15.16-.26.25-.43.08-.18.04-.33-.02-.46-.06-.13-.56-1.36-.77-1.86-.2-.5-.4-.42-.55-.43h-.47a.9.9 0 0 0-.66.3c-.23.25-.86.85-.86 2.07s.88 2.4 1 2.57c.13.18 1.74 2.65 4.2 3.71.59.25 1.05.4 1.41.51.59.19 1.13.16 1.55.1.47-.07 1.5-.61 1.71-1.21.21-.6.21-1.11.15-1.21-.06-.1-.23-.16-.49-.28z" />
                                        </svg>
                                        Open WhatsApp
                                    </a>
                                )}
                            </div>
                        ) : (
                            <div className="text-sm text-muted-foreground italic">No phone on file</div>
                        )}
                    </div>
                </div>

                <div className="mt-6 pt-4 border-t border-white/5 text-[10px] uppercase tracking-[0.25em] text-muted-foreground">
                    Pro hasn't submitted any documents yet
                </div>
            </div>
        </div>
    );
}


function VerificationBadge({ level, label, daysRemaining }) {
    if (!level || level === "none") {
        return (
            <Badge className="bg-white/5 text-muted-foreground border border-white/10 uppercase text-[9px] tracking-[0.2em]">
                Not verified
            </Badge>
        );
    }
    const styles = {
        provisional: "bg-amber-900/40 text-amber-300 border border-amber-700/40",
        royal: "bg-primary/15 gold-text border border-primary/50",
        suspended: "bg-rose-900/40 text-rose-300 border border-rose-700/50",
    };
    const short = {
        provisional: "Provisional",
        royal: "Royal",
        suspended: "Suspended",
    };
    return (
        <div className="flex flex-col gap-1" data-testid={`verification-badge-${level}`}>
            <Badge className={`${styles[level] || styles.provisional} uppercase text-[9px] tracking-[0.2em] w-fit`}>
                {short[level] || label}
            </Badge>
            {level === "provisional" && typeof daysRemaining === "number" && daysRemaining >= 0 && (
                <span className={`text-[9px] uppercase tracking-[0.2em] ${daysRemaining <= 5 ? "text-rose-300 font-semibold" : "text-muted-foreground"}`}>
                    {daysRemaining}d left
                </span>
            )}
        </div>
    );
}



function LeadsTab({ leads, stats, onExport }) {
    return (
        <div className="space-y-6" data-testid="admin-leads-content">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-px bg-white/5">
                <LeadStat label="Total leads" value={stats?.total ?? leads.length} />
                <LeadStat label="Nudged" value={stats?.nudged ?? 0} />
                <LeadStat label="Converted" value={stats?.converted ?? 0} />
                <LeadStat label="Conversion %" value={`${stats?.conversion_rate ?? 0}%`} />
            </div>

            <div className="flex items-center justify-between flex-wrap gap-3">
                <div className="text-xs text-muted-foreground">
                    <span className="text-foreground font-medium">{leads.length}</span> visitor lead{leads.length === 1 ? "" : "s"} on file · ROYAL10 promo nudge auto-fired on capture
                </div>
                <Button
                    size="sm"
                    variant="outline"
                    onClick={onExport}
                    disabled={leads.length === 0}
                    className="rounded-sm uppercase tracking-[0.2em] text-[10px] h-8 border-white/15"
                    data-testid="export-leads-csv"
                >
                    <Download size={12} className="mr-1" /> Export Leads CSV
                </Button>
            </div>

            <div className="bg-card border border-white/10 overflow-x-auto">
                <Table data-testid="admin-leads-table">
                    <TableHeader>
                        <TableRow className="border-white/10 hover:bg-transparent">
                            <TableHead className="overline">Email</TableHead>
                            <TableHead className="overline">Phone</TableHead>
                            <TableHead className="overline">Source</TableHead>
                            <TableHead className="overline">Promo</TableHead>
                            <TableHead className="overline">Nudge</TableHead>
                            <TableHead className="overline">Touches</TableHead>
                            <TableHead className="overline">Converted</TableHead>
                            <TableHead className="overline">Captured</TableHead>
                        </TableRow>
                    </TableHeader>
                    <TableBody>
                        {leads.map((l) => (
                            <TableRow key={l.id} className="border-white/5">
                                <TableCell className="text-xs">{l.email || "—"}</TableCell>
                                <TableCell className="text-xs text-muted-foreground">{l.phone || "—"}</TableCell>
                                <TableCell className="text-xs text-muted-foreground uppercase tracking-[0.15em]">{l.source || "—"}</TableCell>
                                <TableCell className="text-xs gold-text font-mono">{l.promo_code || "—"}</TableCell>
                                <TableCell>
                                    {l.nudge_email_sent ? (
                                        <Badge className="bg-emerald-900/40 text-emerald-300 border border-emerald-700/40 uppercase text-[9px] tracking-[0.2em]">Sent</Badge>
                                    ) : (
                                        <Badge className="bg-white/5 text-muted-foreground border border-white/10 uppercase text-[9px] tracking-[0.2em]">Pending</Badge>
                                    )}
                                </TableCell>
                                <TableCell className="text-xs">{l.touches || 1}</TableCell>
                                <TableCell>
                                    {l.converted_to_user ? (
                                        <Badge className="bg-primary/15 gold-text border border-primary/50 uppercase text-[9px] tracking-[0.2em]">Royal</Badge>
                                    ) : (
                                        <span className="text-muted-foreground text-xs">—</span>
                                    )}
                                </TableCell>
                                <TableCell className="text-xs text-muted-foreground">
                                    {l.created_at ? format(parseISO(l.created_at), "MMM d, HH:mm") : "—"}
                                </TableCell>
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
                {leads.length === 0 && (
                    <div className="p-12 text-center text-muted-foreground">No visitor leads captured yet — popup fires after 8s on the landing page.</div>
                )}
            </div>
        </div>
    );
}

function LeadStat({ label, value }) {
    return (
        <div className="bg-card p-6">
            <div className="overline mb-2">{label}</div>
            <div className="font-serif text-3xl tracking-tight">{value}</div>
        </div>
    );
}

function FinanceCard({ label, value, testid, accent }) {
    const accentClass =
        accent === "gold" ? "gold-text"
        : accent === "warn" ? "text-amber-300"
        : "text-foreground";
    return (
        <div className="bg-card border border-white/10 p-5" data-testid={testid}>
            <div className="overline mb-2">{label}</div>
            <div className={`font-serif text-2xl tracking-tight ${accentClass}`}>{value}</div>
        </div>
    );
}


function DisputesTab({ disputes, filter, onFilter, onResolve }) {
    const CATS = ["payment", "quality", "no_show", "safety", "behaviour", "other"];
    const list = Array.isArray(disputes) ? disputes : [];

    return (
        <div className="space-y-4" data-testid="admin-disputes-tab">
            <div className="flex items-baseline justify-between flex-wrap gap-3 mb-5">
                <div>
                    <h3 className="font-serif text-2xl">Dispute Queue</h3>
                    <div className="text-xs text-muted-foreground mt-1">
                        Customer or provider complaints — tap Resolve or Dismiss to clear.
                    </div>
                </div>
                <Select value={filter} onValueChange={onFilter}>
                    <SelectTrigger className="w-[200px] bg-input border-white/10 rounded-sm uppercase text-xs tracking-[0.2em] h-10" data-testid="dispute-filter-select">
                        <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                        <SelectItem value="open">Open</SelectItem>
                        <SelectItem value="needs_more_info">Needs info</SelectItem>
                        <SelectItem value="resolved">Resolved</SelectItem>
                        <SelectItem value="dismissed">Dismissed</SelectItem>
                        <SelectItem value="all">All</SelectItem>
                    </SelectContent>
                </Select>
            </div>

            {list.length === 0 ? (
                <div className="bg-card border border-white/10 p-12 text-center text-muted-foreground" data-testid="admin-disputes-empty">
                    No disputes in this view. 🏆
                </div>
            ) : (
                <div className="space-y-3">
                    {list.map((d) => (
                        <article
                            key={d.id}
                            className="bg-card border border-white/10 p-6 rounded-sm"
                            data-testid={`dispute-row-${d.id}`}
                        >
                            <div className="flex items-start justify-between flex-wrap gap-4 mb-3">
                                <div>
                                    <div className="flex items-center gap-2 flex-wrap">
                                        <Badge className="bg-red-900/40 text-red-300 border border-red-700/40 uppercase text-[10px] tracking-[0.2em]">
                                            {d.category}
                                        </Badge>
                                        <Badge className={`uppercase text-[10px] tracking-[0.2em] border ${
                                            d.status === "open"
                                                ? "bg-amber-900/40 text-amber-300 border-amber-700/40"
                                                : d.status === "resolved"
                                                ? "bg-emerald-900/40 text-emerald-300 border-emerald-700/40"
                                                : d.status === "dismissed"
                                                ? "bg-white/5 text-muted-foreground border-white/10"
                                                : "bg-sky-900/40 text-sky-300 border-sky-700/40"
                                        }`}>
                                            {d.status?.replace("_", " ")}
                                        </Badge>
                                        {["open", "needs_more_info"].includes(d.status) && (
                                            <SLAClock startedAt={d.created_at} compact />
                                        )}
                                        <span className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground">
                                            Opened by {d.opened_by_role} · {d.opened_by_name}
                                        </span>
                                    </div>
                                    <div className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground mt-2">
                                        Booking <span className="font-mono text-foreground">{(d.booking_id || "").slice(0, 8).toUpperCase()}</span>
                                        {" · "}{d.created_at ? format(parseISO(d.created_at), "MMM d · HH:mm") : "—"}
                                    </div>
                                </div>
                            </div>

                            <p className="text-sm text-foreground whitespace-pre-wrap bg-background/40 border border-white/5 rounded-sm p-3 mb-3">
                                {d.details}
                            </p>

                            {d.admin_notes && (
                                <div className="text-xs text-amber-300 bg-amber-500/5 border border-amber-700/30 rounded-sm p-3 mb-3">
                                    <div className="overline mb-1">Admin notes</div>
                                    {d.admin_notes}
                                </div>
                            )}

                            {["open", "needs_more_info"].includes(d.status) && (
                                <div className="flex flex-wrap gap-2 pt-3 border-t border-white/5">
                                    <Button
                                        size="sm"
                                        onClick={() => {
                                            const notes = prompt("Admin notes for the customer (optional):", "") || "";
                                            onResolve(d.id, "resolved_customer", notes);
                                        }}
                                        className="rounded-sm uppercase tracking-[0.2em] text-[10px] h-9 bg-emerald-700 text-white hover:bg-emerald-600"
                                        data-testid={`dispute-resolve-customer-${d.id}`}
                                    >
                                        Resolve for customer
                                    </Button>
                                    <Button
                                        size="sm"
                                        onClick={() => {
                                            const notes = prompt("Admin notes for the provider (optional):", "") || "";
                                            onResolve(d.id, "resolved_provider", notes);
                                        }}
                                        className="rounded-sm uppercase tracking-[0.2em] text-[10px] h-9 bg-sky-700 text-white hover:bg-sky-600"
                                        data-testid={`dispute-resolve-provider-${d.id}`}
                                    >
                                        Resolve for pro
                                    </Button>
                                    <Button
                                        size="sm"
                                        variant="outline"
                                        onClick={() => {
                                            const notes = prompt("Why dismiss?", "") || "";
                                            onResolve(d.id, "dismissed", notes);
                                        }}
                                        className="rounded-sm uppercase tracking-[0.2em] text-[10px] h-9 border-white/15"
                                        data-testid={`dispute-dismiss-${d.id}`}
                                    >
                                        Dismiss
                                    </Button>
                                    <Button
                                        size="sm"
                                        variant="outline"
                                        onClick={() => {
                                            const notes = prompt("What more info do you need?", "") || "";
                                            if (notes.trim()) onResolve(d.id, "needs_more_info", notes);
                                        }}
                                        className="rounded-sm uppercase tracking-[0.2em] text-[10px] h-9 border-white/15"
                                        data-testid={`dispute-need-info-${d.id}`}
                                    >
                                        Need info
                                    </Button>
                                </div>
                            )}
                        </article>
                    ))}
                </div>
            )}
        </div>
    );
}

function ConciergeTab({ handoffs, filter, onFilter, onUpdate }) {
    const list = Array.isArray(handoffs) ? handoffs : [];

    return (
        <div className="space-y-4" data-testid="admin-concierge-tab">
            <div className="flex items-baseline justify-between flex-wrap gap-3 mb-5">
                <div>
                    <h3 className="font-serif text-2xl">Concierge Handoffs</h3>
                    <div className="text-xs text-muted-foreground mt-1">
                        Tickets the Royal Concierge couldn't close — reply directly from here.
                    </div>
                </div>
                <Select value={filter} onValueChange={onFilter}>
                    <SelectTrigger className="w-[180px] bg-input border-white/10 rounded-sm uppercase text-xs tracking-[0.2em] h-10" data-testid="handoff-filter-select">
                        <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                        <SelectItem value="open">Open</SelectItem>
                        <SelectItem value="in_progress">In progress</SelectItem>
                        <SelectItem value="closed">Closed</SelectItem>
                        <SelectItem value="all">All</SelectItem>
                    </SelectContent>
                </Select>
            </div>

            {list.length === 0 ? (
                <div className="bg-card border border-white/10 p-12 text-center text-muted-foreground" data-testid="admin-concierge-empty">
                    No tickets in this view.
                </div>
            ) : (
                <div className="space-y-3">
                    {list.map((t) => (
                        <article
                            key={t.id}
                            className="bg-card border border-white/10 p-6 rounded-sm"
                            data-testid={`handoff-row-${t.id}`}
                        >
                            <div className="flex items-start justify-between flex-wrap gap-4 mb-3">
                                <div className="flex-1 min-w-0">
                                    <div className="flex items-center gap-2 flex-wrap">
                                        <Badge className={`uppercase text-[10px] tracking-[0.2em] border ${
                                            t.status === "open"
                                                ? "bg-amber-900/40 text-amber-300 border-amber-700/40"
                                                : t.status === "in_progress"
                                                ? "bg-sky-900/40 text-sky-300 border-sky-700/40"
                                                : "bg-white/5 text-muted-foreground border-white/10"
                                        }`}>
                                            {t.status?.replace("_", " ")}
                                        </Badge>
                                        {t.status === "open" && (
                                            <SLAClock startedAt={t.created_at} compact />
                                        )}
                                        {t.email && (
                                            <span className="text-xs text-muted-foreground">
                                                {t.email}
                                            </span>
                                        )}
                                        {t.phone && (
                                            <span className="text-xs text-muted-foreground">
                                                · {t.phone}
                                            </span>
                                        )}
                                    </div>
                                    <div className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground mt-2">
                                        Ticket <span className="font-mono text-foreground">{(t.id || "").slice(0,8).toUpperCase()}</span>
                                        {" · "}{t.created_at ? format(parseISO(t.created_at), "MMM d · HH:mm") : "—"}
                                    </div>
                                </div>
                            </div>

                            <div className="mb-3">
                                <div className="overline mb-1">Question</div>
                                <p className="text-sm text-foreground whitespace-pre-wrap bg-background/40 border border-white/5 rounded-sm p-3">
                                    {t.question}
                                </p>
                            </div>

                            {t.transcript && t.transcript.length > 0 && (
                                <details className="mb-3">
                                    <summary className="cursor-pointer overline hover:text-foreground">
                                        Transcript ({t.transcript.length} message{t.transcript.length === 1 ? "" : "s"})
                                    </summary>
                                    <div className="mt-2 bg-background/40 border border-white/5 rounded-sm p-3 max-h-60 overflow-y-auto space-y-2">
                                        {t.transcript.map((m, i) => (
                                            <div key={`transcript-${t.id}-${i}`} className="text-xs">
                                                <span className={`font-mono uppercase text-[9px] tracking-[0.2em] mr-2 ${
                                                    m.role === "user" ? "text-[#FFD700]" : "text-muted-foreground"
                                                }`}>
                                                    {m.role}:
                                                </span>
                                                <span className="text-foreground">{m.content}</span>
                                            </div>
                                        ))}
                                    </div>
                                </details>
                            )}

                            {t.admin_notes && (
                                <div className="text-xs text-amber-300 bg-amber-500/5 border border-amber-700/30 rounded-sm p-3 mb-3">
                                    <div className="overline mb-1">Admin notes</div>
                                    {t.admin_notes}
                                </div>
                            )}

                            {t.status !== "closed" && (
                                <div className="flex flex-wrap gap-2 pt-3 border-t border-white/5">
                                    {t.status === "open" && (
                                        <Button
                                            size="sm"
                                            onClick={() => {
                                                const notes = prompt("Admin notes (optional):", t.admin_notes || "") || "";
                                                onUpdate(t.id, "in_progress", notes);
                                            }}
                                            className="rounded-sm uppercase tracking-[0.2em] text-[10px] h-9 bg-sky-700 text-white hover:bg-sky-600"
                                            data-testid={`handoff-start-${t.id}`}
                                        >
                                            Pick up
                                        </Button>
                                    )}
                                    <Button
                                        size="sm"
                                        onClick={() => {
                                            const notes = prompt("Resolution notes (what did you tell them?):", t.admin_notes || "") || "";
                                            onUpdate(t.id, "closed", notes);
                                        }}
                                        className="rounded-sm uppercase tracking-[0.2em] text-[10px] h-9 bg-emerald-700 text-white hover:bg-emerald-600"
                                        data-testid={`handoff-close-${t.id}`}
                                    >
                                        Mark closed
                                    </Button>
                                    {t.email && (
                                        <a
                                            href={`mailto:${t.email}?subject=SMART%20Royal%20Concierge%20follow-up&body=Hi%20Royal%2C%0A%0AThanks%20for%20reaching%20out%20to%20the%20Concierge.%0A`}
                                            className="inline-flex items-center justify-center rounded-sm uppercase tracking-[0.2em] text-[10px] h-9 px-3 border border-[#FFD700]/50 text-[#FFD700] hover:bg-[#FFD700]/10"
                                            data-testid={`handoff-email-${t.id}`}
                                        >
                                            Email reply
                                        </a>
                                    )}
                                </div>
                            )}
                        </article>
                    ))}
                </div>
            )}
        </div>
    );
}

